import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { deskApi } from '@/api';
import { PageHeader } from '@/components/layout/PageHeader';
import type { CustomerSupport360 } from '@/types/desk';
import { showApiError } from '@/utils/apiError';

export function CustomerSupport360Page() {
  const { customerId } = useParams<{ customerId: string }>();
  const [data, setData] = useState<CustomerSupport360 | null>(null);

  useEffect(() => {
    if (!customerId) return;
    deskApi
      .support360(Number(customerId))
      .then(setData)
      .catch((err) => showApiError(err, 'Failed to load Customer Support 360'));
  }, [customerId]);

  if (!data) {
    return <div className="p-6 text-slate-500">Loading support profile…</div>;
  }

  const profile = data.crmProfile.profile;

  return (
    <div>
      <PageHeader
        title={profile.name}
        description="Customer Support 360 — tickets, CRM, finance, and omnichannel history"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Desk', to: '/desk' },
          { label: 'Support 360' },
        ]}
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 mb-4">
        <SummaryCard label="Open tickets" value={data.openTickets} />
        <SummaryCard label="Total tickets" value={data.totalTickets} />
        <SummaryCard label="Invoices" value={data.invoiceCount} />
        <SummaryCard label="Avg CSAT" value={data.averageCsat != null ? data.averageCsat.toFixed(1) : '—'} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="panel">
          <h3 className="font-semibold mb-2">Support tickets</h3>
          <ul className="text-sm space-y-2">
            {data.tickets.map((t) => (
              <li key={t.id}>
                <Link to={`/desk/tickets/${t.id}`} className="text-primary-600 hover:underline font-medium">
                  {t.ticketNumber}
                </Link>
                <span className="text-slate-500"> — {t.subject}</span>
              </li>
            ))}
            {data.tickets.length === 0 && <li className="text-slate-500">No tickets</li>}
          </ul>
        </div>
        <div className="panel">
          <h3 className="font-semibold mb-2">Deals (CRM)</h3>
          <ul className="text-sm space-y-1">
            {data.deals.map((d) => (
              <li key={d.id}>{d.dealName} — {d.dealStatus}</li>
            ))}
            {data.deals.length === 0 && <li className="text-slate-500">No deals</li>}
          </ul>
        </div>
      </div>

      <div className="mt-4 panel">
        <h3 className="font-semibold mb-3">Unified support timeline</h3>
        <ul className="space-y-2 max-h-80 overflow-y-auto text-sm">
          {data.supportTimeline.map((item, i) => (
            <li key={i} className="border-l-2 border-primary-200 pl-3">
              <span className="text-slate-500">{item.itemType}</span> — {item.title}
              {item.body && <p className="text-slate-600">{item.body}</p>}
            </li>
          ))}
          {data.supportTimeline.length === 0 && <li className="text-slate-500">No activity</li>}
        </ul>
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="panel">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="text-2xl font-semibold text-slate-800">{value}</p>
    </div>
  );
}
